import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import {
  createSpeechToTextService,
  checkBrowserCapability,
  MODEL_IDS,
  VoiceState,
  canStartListening,
  isRecordingState,
} from "../../voice";
import { useTranslation } from "react-i18next";

const TT_PHRASES = [
  { lang: "en", text: "How can I improve my forehand topspin?" },
  { lang: "en", text: "Why am I losing balance after my forehand?" },
  { lang: "fr", text: "Comment améliorer mon coup droit?" },
  { lang: "fr", text: "Je n'arrive pas à contrôler mon revers." },
  { lang: "es", text: "Cómo genero más topspin en el drive?" },
  { lang: "de", text: "Wie verbessere ich meinen Vorhand-Topspin?" },
];

/**
 * Phase 1 spike: local Whisper latency / accuracy harness.
 * Route: /voice-test (auth-gated).
 */
export default function VoiceTestPage() {
  const { i18n } = useTranslation();
  const [modelId, setModelId] = useState(MODEL_IDS.base);
  const [state, setState] = useState(VoiceState.IDLE);
  const [partial, setPartial] = useState("");
  const [finalText, setFinalText] = useState("");
  const [rawText, setRawText] = useState("");
  const [language, setLanguage] = useState(null);
  const [level, setLevel] = useState(0);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [log, setLog] = useState([]);
  const serviceRef = useRef(null);

  const capability = useMemo(() => checkBrowserCapability(), []);

  const appendLog = useCallback((line) => {
    setLog((prev) => [`${new Date().toISOString().slice(11, 19)}  ${line}`, ...prev].slice(0, 40));
  }, []);

  const rebuildService = useCallback(
    (id) => {
      if (serviceRef.current) serviceRef.current.dispose();
      const svc = createSpeechToTextService({ modelId: id, engine: "whisper" });
      svc.onState((s) => setState(s));
      svc.onPartial((t) => setPartial(t || ""));
      svc.onLevel((l) => setLevel(l));
      svc.onProgress((p) => setProgress(p));
      svc.onError((err) => {
        setError(err);
        appendLog(`ERROR ${err.code}: ${err.message}`);
      });
      svc.onFinal((result) => {
        setFinalText(result.text || "");
        setRawText(result.rawText || "");
        setLanguage(result.language);
        setMetrics(result.metrics || svc.lastMetrics);
        appendLog(
          `FINAL (${result.metrics?.transcribeMs ?? "?"}ms): ${result.text}`
        );
      });
      serviceRef.current = svc;
      setFinalText("");
      setRawText("");
      setPartial("");
      setError(null);
      setMetrics(null);
      setProgress(null);
      setState(VoiceState.IDLE);
    },
    [appendLog]
  );

  useEffect(() => {
    rebuildService(modelId);
    return () => {
      if (serviceRef.current) serviceRef.current.dispose();
    };
  }, [modelId, rebuildService]);

  const languageHint = (i18n.language || "en").substring(0, 2);

  const handleInit = async () => {
    setError(null);
    appendLog(`Loading model ${modelId}…`);
    try {
      await serviceRef.current.initialize();
      appendLog(`Model ready (${serviceRef.current.lastMetrics?.modelLoadMs ?? "?"}ms)`);
      setMetrics(serviceRef.current.lastMetrics);
    } catch {
      /* onError handles */
    }
  };

  const handleToggle = async () => {
    setError(null);
    if (isRecordingState(state)) {
      appendLog("Stopping…");
      await serviceRef.current.stopListening();
      return;
    }
    if (!canStartListening(state) && state !== VoiceState.LOADING) return;
    setFinalText("");
    setRawText("");
    setPartial("");
    appendLog("Listening…");
    await serviceRef.current.startListening({ languageHint });
  };

  const recording = isRecordingState(state);
  const busy = state === VoiceState.LOADING || state === VoiceState.PROCESSING;

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: "auto", pb: 12 }}>
      <Typography variant="h5" gutterBottom>
        Voice STT test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Local Whisper only — audio never leaves this device. Use this page for
        iPhone Safari go/no-go benchmarks before Coach mic integration.
      </Typography>

      {!capability.ok && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unsupported browser: {capability.reason}
        </Alert>
      )}
      {capability.details?.lowMemoryWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Low deviceMemory reported — prefer tiny/base quantized models.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel id="model-label">Model</InputLabel>
            <Select
              labelId="model-label"
              label="Model"
              value={modelId}
              disabled={recording || busy}
              onChange={(e) => setModelId(e.target.value)}
            >
              <MenuItem value={MODEL_IDS.tiny}>whisper-tiny</MenuItem>
              <MenuItem value={MODEL_IDS.base}>whisper-base — default</MenuItem>
              <MenuItem value={MODEL_IDS.small}>whisper-small</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`state: ${state}`}
              color={recording ? "error" : state === VoiceState.IDLE ? "default" : "primary"}
            />
            <Chip
              size="small"
              label={`COI: ${capability.details.crossOriginIsolated ? "yes" : "no"}`}
            />
            <Chip
              size="small"
              label={`SAB: ${capability.details.sharedArrayBuffer ? "yes" : "no"}`}
            />
            <Chip size="small" label={`MIME: ${capability.details.mimeType || "default"}`} />
          </Stack>

          {progress && state === VoiceState.LOADING && (
            <Box>
              <Typography variant="caption">
                Downloading… {progress.percent}% {progress.file || ""}
              </Typography>
              <LinearProgress variant="determinate" value={progress.percent || 0} />
            </Box>
          )}

          <Box>
            <Typography variant="caption">Mic level</Typography>
            <LinearProgress
              variant="determinate"
              value={Math.round(level * 100)}
              sx={{ height: 10, borderRadius: 1 }}
              color={recording ? "error" : "primary"}
            />
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleInit} disabled={busy || recording}>
              Load model
            </Button>
            <Button
              variant="contained"
              color={recording ? "error" : "primary"}
              startIcon={recording ? <StopIcon /> : <MicIcon />}
              onClick={handleToggle}
              disabled={!capability.ok || state === VoiceState.LOADING || state === VoiceState.PROCESSING}
            >
              {recording ? "Stop" : state === VoiceState.PROCESSING ? "Transcribing…" : "Record"}
            </Button>
          </Stack>

          {error && (
            <Alert severity="error">
              {error.code}: {error.message}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2">Partial</Typography>
            <Typography variant="body2" color="text.secondary">
              {partial || "—"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Raw Whisper</Typography>
            <Typography variant="body1">{rawText || "—"}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Normalized (TT vocab)</Typography>
            <Typography variant="body1" fontWeight={600}>
              {finalText || "—"}
            </Typography>
          </Box>
          <Typography variant="caption">
            Detected / hint language: {language || languageHint}
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Metrics
        </Typography>
        <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: "pre-wrap" }}>
          {metrics ? JSON.stringify(metrics, null, 2) : "No run yet"}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Target: 5–10s utterance → finalize &lt;2–3s after stop; model download once; 0 UI freeze.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          SmartTT phrase checklist
        </Typography>
        <Stack spacing={0.5}>
          {TT_PHRASES.map((p) => (
            <Typography key={p.text} variant="body2">
              [{p.lang}] {p.text}
            </Typography>
          ))}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Log
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 200, overflow: "auto" }}>
          {log.map((line, i) => (
            <Typography component="li" key={i} variant="caption" display="block">
              {line}
            </Typography>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}
